/**
 * VendorEntityCard - Summary card for a vendor-managed entity.
 * Handles live check-in toggle with optional close-time picker.
 * @atomic @idempotent
 */

import { memo, useState, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChatBubbleLeftRightIcon, CheckBadgeIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { checkInAPI } from '../../services/api/checkin'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { trackButtonClick } from '../../utils/analytics'
import LiveStatusChip from '../map/LiveStatusChip'
import VendorBroadcastModal from './VendorBroadcastModal'
import VendorStewardshipRelease from './VendorStewardshipRelease'
import { toast } from '../../services/toastService'

const logger = createLogger('VendorEntityCard')

/** Convert ISO datetime → local HH:MM for time input (empty string when missing or expired). */
const toLocalTime = (isoStr) => {
  if (!isoStr) return ''
  const d = new Date(isoStr)
  if (d <= new Date()) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Build ISO string from HH:MM, rolling to next day if the time has already passed. */
const buildISO = (timeStr) => {
  if (!timeStr) return undefined
  const [hh, mm] = timeStr.split(':').map(Number)
  const d = new Date()
  d.setHours(hh, mm, 0, 0)
  if (d <= new Date()) d.setDate(d.getDate() + 1)
  return d.toISOString()
}

const VendorEntityCard = memo(function VendorEntityCard({
  entity,
  isSelected,
  onSelect,
  onOpenChannel,
  onReleased,
}) {
  const { t } = useTranslation()
  const { execute, loading } = useAsyncOperation()
  const { execute: executeUpdate, loading: updateLoading } = useAsyncOperation()
  const [isLive, setIsLive] = useState(entity?.isLive ?? false)
  const [liveUntil, setLiveUntil] = useState(() => toLocalTime(entity?.liveUntil))
  const [liveUntilTouched, setLiveUntilTouched] = useState(false)
  const [showBroadcast, setShowBroadcast] = useState(false)

  // Sync when parent re-fetches entity data
  useEffect(() => {
    setIsLive(entity?.isLive ?? false)
    setLiveUntil(toLocalTime(entity?.liveUntil))
    setLiveUntilTouched(false)
  }, [entity?.isLive, entity?.liveUntil])

  const handleSelect = () => onSelect?.(entity)
  const handleChannel = (e) => { e.stopPropagation(); onOpenChannel?.(entity) }

  const setTime = useCallback((val) => {
    setLiveUntil(val)
    setLiveUntilTouched(true)
  }, [])

  /** Add N hours from now to the close-time picker. */
  const addHours = useCallback((h) => {
    const d = new Date(Date.now() + h * 3600 * 1000)
    setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`)
    trackButtonClick(`vendor_close_time_plus_${h}h`, 'VendorEntityCard', { entity_id: entity.id })
  }, [setTime, entity.id])

  /** Toggle open/closed. Includes liveUntil when checking in. */
  const handleCheckIn = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const next = !isLive
    const liveUntilISO = next ? buildISO(liveUntil) : undefined
    trackButtonClick(next ? 'vendor_check_in' : 'vendor_check_out', 'VendorEntityCard', {
      entity_id: entity.id, has_close_time: !!liveUntilISO
    })
    execute(() => checkInAPI.checkIn(entity.id, next, liveUntilISO), {
      onSuccess: () => {
        setIsLive(next)
        if (!next) setLiveUntil('')
        setLiveUntilTouched(false)
        toast.success(next ? t('checkin.nowOpen') : t('checkin.nowClosed'))
        logger.info('Check-in toggled', { entityId: entity.id, isLive: next })
      },
      onError: (err) => {
        logger.error('Check-in failed', err)
        toast.error(err?.message || t('checkin.error'))
      },
    })
  }, [isLive, liveUntil, entity, execute, t])

  /** Update close time while already live (separate from the toggle). */
  const handleUpdateCloseTime = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const liveUntilISO = buildISO(liveUntil) // undefined → clear close time
    trackButtonClick('vendor_update_close_time', 'VendorEntityCard', {
      entity_id: entity.id, has_close_time: !!liveUntilISO
    })
    executeUpdate(() => checkInAPI.checkIn(entity.id, true, liveUntilISO), {
      onSuccess: () => {
        setLiveUntilTouched(false)
        toast.success(t('checkin.nowOpen'))
        logger.info('Close time updated', { entityId: entity.id })
      },
      onError: (err) => {
        logger.error('Close time update failed', err)
        toast.error(err?.message || t('checkin.error'))
      },
    })
  }, [liveUntil, entity, executeUpdate, t])

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={(e) => e.key === 'Enter' && handleSelect()}
      className={`relative rounded-xl border p-3 cursor-pointer transition-all outline-none focus:outline-none ${
        isSelected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'border-gray-200 bg-white hover:border-primary/40'
      }`}
    >
      {/* Entity info */}
      <div className="flex items-start gap-3">
        {entity.imageUrl && (
          <img src={entity.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0 pointer-events-none" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Link
              to={`/entity/${entity.id}`}
              onClick={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              className="font-semibold text-gray-900 hover:text-primary truncate text-sm transition-colors"
            >
              {entity.name}
            </Link>
            {entity.isVerified && (
              <CheckBadgeIcon className="h-4 w-4 text-primary flex-shrink-0" aria-label="Verified" />
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 capitalize">{entity.category?.replace('_', ' ')}</p>
          {entity.currentAddress && <p className="text-xs text-gray-400 truncate mt-1">{entity.currentAddress}</p>}
          {entity.offeringCount > 0 && (
            <p className="text-xs text-primary mt-1">
              {t('vendor.menu.itemCount', { count: entity.offeringCount }, `${entity.offeringCount} menu items`)}
            </p>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-2.5 flex gap-1.5">
        <button
          type="button"
          onClick={handleChannel}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleChannel(e) }}
          className="flex items-center justify-center gap-1 py-1.5 px-2.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        >
          <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{t('vendor.entityCard.channel', 'Channel')}</span>
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setShowBroadcast(true) }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setShowBroadcast(true) }}
          className="flex items-center justify-center py-1.5 px-2.5 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
          title={t('vendor.broadcast.title', 'Broadcast to followers')}
        >
          {t('vendor.entityCard.broadcast', 'Broadcast')}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={handleCheckIn}
          onTouchEnd={handleCheckIn}
          className={[
            'flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium transition-colors',
            isLive
              ? 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200'
              : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200',
            loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <span className={['w-2 h-2 rounded-full flex-shrink-0', isLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'].join(' ')} />
          {loading
            ? t('common.saving', 'Saving…')
            : isLive
            ? t('checkin.checkOut', 'Close')
            : t('checkin.checkIn', 'Open now')}
        </button>
      </div>

      {/* Live status chip */}
      {isLive && <div className="mt-1.5"><LiveStatusChip isLive liveUntil={entity?.liveUntil} /></div>}

      {/* Close-time picker — only shown when live */}
      {isLive && (
        <div className="mt-2 space-y-1.5">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-400 flex-shrink-0">
            {isLive ? t('checkin.updateCloseTime', 'Update close time:') : t('checkin.setCloseTime', 'Close at:')}
          </label>
          <input
            type="time"
            value={liveUntil}
            onChange={(e) => setTime(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-primary/60"
          />
        </div>

        {/* Quick-duration shortcuts */}
        <div className="flex gap-1">
          {[1, 2, 3].map(h => (
            <button
              key={h}
              type="button"
              onClick={(e) => { e.stopPropagation(); addHours(h) }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); addHours(h) }}
              className="flex-1 text-[11px] py-1 rounded border border-gray-200 text-gray-500 hover:bg-primary/5 hover:border-primary/40 hover:text-primary transition-colors"
            >
              +{h}h
            </button>
          ))}
          {liveUntil && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setTime('') }}
              onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); setTime('') }}
              className="px-2 text-[11px] py-1 rounded border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            >
              ✕
            </button>
          )}
        </div>

        {/* Update button — only while live and user has changed the picker */}
        {isLive && liveUntilTouched && (
          <button
            type="button"
            disabled={updateLoading}
            onClick={handleUpdateCloseTime}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleUpdateCloseTime(e) }}
            className="w-full text-xs py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-60"
          >
            {updateLoading ? t('common.saving', 'Saving…') : t('checkin.updateCloseTime', 'Update close time')}
          </button>
        )}
        </div>
      )}

      {/* Broadcast modal */}
      {showBroadcast && (
        <VendorBroadcastModal
          entityId={entity.id}
          entityName={entity.name}
          onClose={() => setShowBroadcast(false)}
        />
      )}

      <VendorStewardshipRelease entity={entity} onReleased={onReleased} />
    </div>
  )
})

export default VendorEntityCard
